import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { ToggleSwitch } from '@/components/ui/toggleSwitch';
import {
  useAssistantUserPreferencesQuery,
  useUpdateAssistantUserPreferencesMutation,
} from '@/hooks/useAssistantProactive';
import { useIsAssistantAvailable } from '@/hooks/useIsAssistantAvailable';

interface PreferenceRowProps {
  label: string;
  hint: string;
  isChecked: boolean;
  isDisabled: boolean;
  onToggle: () => void;
}

function PreferenceRow({ label, hint, isChecked, isDisabled, onToggle }: PreferenceRowProps) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <p className="text-sm font-semibold text-zooindigo">{label}</p>
        <p className="text-xs text-zoomuted mt-0.5">{hint}</p>
      </div>
      <div className="shrink-0">
        <ToggleSwitch
          isChecked={isChecked}
          accessibleLabel={label}
          isDisabled={isDisabled}
          onToggle={onToggle}
        />
      </div>
    </div>
  );
}

export function AssistantPreferencesSection() {
  const { t } = useTranslation('assistant');
  const isAvailable = useIsAssistantAvailable();
  const preferencesQuery = useAssistantUserPreferencesQuery();
  const updatePreferences = useUpdateAssistantUserPreferencesMutation();
  const preferences = preferencesQuery.data;
  if (
    isAvailable === false ||
    preferences === undefined ||
    !preferences.workspaceProactiveEnabled
  ) {
    return null;
  }
  return (
    <Card padding="lg">
      <h2 className="text-lg font-bold text-zooindigo">{t('myPreferences.heading')}</h2>
      <div className="mt-6 space-y-5">
        <PreferenceRow
          label={t('myPreferences.badgeAndTips')}
          hint={t('myPreferences.badgeAndTipsHint')}
          isChecked={preferences.proactiveEnabled}
          isDisabled={updatePreferences.isPending}
          onToggle={function toggleBadgeAndTips() {
            updatePreferences.mutate({ proactiveEnabled: preferences.proactiveEnabled === false });
          }}
        />
        <PreferenceRow
          label={t('myPreferences.autoBrief')}
          hint={t('myPreferences.autoBriefHint')}
          isChecked={preferences.autoBriefEnabled}
          isDisabled={updatePreferences.isPending || preferences.proactiveEnabled === false}
          onToggle={function toggleAutoBrief() {
            updatePreferences.mutate({ autoBriefEnabled: preferences.autoBriefEnabled === false });
          }}
        />
      </div>
    </Card>
  );
}
